import React, { useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'sonner';
import { Download } from '@phosphor-icons/react';
import i18n from '../i18n';

/**
 * <FunnelFlowchart> — premium visualisation of any normalised funnel.
 *
 * Props:
 *   stages: Array<{
 *     id: string,
 *     label: string,                       // bold title shown on node
 *     stage_type: 'magnet'|'landing'|'vsl'|'webinar'|'email'|'pitch'|'upsell'|'downsell'|'thank_you'|'custom',
 *     description?: string,                // body line
 *     copy_excerpt?: string,               // small italic blockquote
 *     cta?: string,                        // button-style chip
 *     meta?: string,                       // top-right tag (e.g. "5 emails", "$97")
 *   }>
 *   title?: string                         // optional caption shown above the canvas
 *   testId?: string
 *   height?: string                        // CSS height (default 480px)
 *   accent?: string                        // hex for download button + frame accent
 */

const STAGE_THEME = {
  magnet:    { color: '#10B981', icon: '🧲', label: 'Lead Magnet' },
  landing:   { color: '#2563EB', icon: '🌐', label: 'Landing' },
  vsl:       { color: '#0EA5E9', icon: '🎥', label: 'VSL' },
  webinar:   { color: '#06B6D4', icon: '🎙', label: 'Webinar' },
  email:     { color: '#A855F7', icon: '✉️', label: 'Email' },
  pitch:     { color: '#F59E0B', icon: '💰', label: 'Pitch' },
  upsell:    { color: '#EF4444', icon: '⬆️', label: 'Upsell' },
  downsell:  { color: '#FB923C', icon: '⬇️', label: 'Downsell' },
  thank_you: { color: '#10B981', icon: '✅', label: 'Thank You' },
  custom:    { color: '#9CA3AF', icon: '◆',  label: '' },
};

function FunnelNode({ data }) {
  const theme = STAGE_THEME[data.stage_type] || STAGE_THEME.custom;
  return (
    <div
      className="rounded-xl shadow-lg backdrop-blur-sm"
      style={{
        background: 'rgba(15,15,15,0.95)',
        border: `1.5px solid ${theme.color}55`,
        minWidth: 220,
        maxWidth: 280,
        padding: '14px 16px',
      }}
      data-testid={`flow-node-${data.id}`}
    >
      <Handle type="target" position={Position.Left} style={{ background: theme.color, width: 8, height: 8, border: 'none' }} />
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <span style={{ fontSize: 14 }}>{theme.icon}</span>
          <span
            className="text-[9px] uppercase tracking-[0.18em] font-bold"
            style={{ color: theme.color }}
          >
            {theme.label || data.stage_type}
          </span>
        </div>
        {data.meta && (
          <span className="text-[10px] text-white/45 font-medium">{data.meta}</span>
        )}
      </div>
      <div className="text-sm font-semibold text-white leading-snug mb-1">{data.label}</div>
      {data.description && (
        <p className="text-[11px] text-white/55 leading-relaxed line-clamp-3">{data.description}</p>
      )}
      {data.copy_excerpt && (
        <div
          className="mt-2 text-[10px] italic border-l-2 pl-2 line-clamp-2"
          style={{ borderColor: `${theme.color}88`, color: '#cccccc' }}
        >
          “{data.copy_excerpt}”
        </div>
      )}
      {data.cta && (
        <div
          className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
          style={{ background: `${theme.color}22`, color: theme.color }}
        >
          ▶ {data.cta}
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: theme.color, width: 8, height: 8, border: 'none' }} />
    </div>
  );
}

const nodeTypes = { funnelNode: FunnelNode };

function stagesToFlow(stages) {
  const COL = 320;
  const nodes = stages.map((s, i) => ({
    id: s.id,
    type: 'funnelNode',
    position: { x: i * COL, y: 0 },
    data: { ...s, id: s.id },
  }));
  const edges = stages.slice(0, -1).map((s, i) => ({
    id: `e-${s.id}-${stages[i + 1].id}`,
    source: s.id,
    target: stages[i + 1].id,
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#444', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#666', width: 18, height: 18 },
  }));
  return { nodes, edges };
}

function FlowchartInner({ stages, accent, downloadName }) {
  const flowRef = useRef(null);
  const { fitView, getNodes } = useReactFlow();
  const { nodes, edges } = useMemo(() => stagesToFlow(stages), [stages]);

  const handleDownload = useCallback(async () => {
    try {
      // Lazy-import html-to-image only if available; otherwise fall back to JSON download.
      const mod = await import('html-to-image').catch(() => null);
      if (!mod || !flowRef.current) {
        const blob = new Blob([JSON.stringify({ nodes: getNodes() }, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${downloadName || 'funnel-flow'}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(i18n.t('flow.exported_json'));
        return;
      }
      fitView({ padding: 0.2 });
      await new Promise((r) => setTimeout(r, 250));
      const dataUrl = await mod.toPng(flowRef.current.querySelector('.react-flow__viewport'), {
        backgroundColor: '#050505',
        pixelRatio: 2,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${downloadName || 'funnel-flow'}.png`;
      a.click();
      toast.success(i18n.t('flow.exported_png'));
    } catch (e) {
      toast.error(i18n.t('flow.export_fail'));
    }
  }, [fitView, getNodes, downloadName]);

  return (
    <>
      <div className="absolute top-3 right-3 z-10 flex gap-2">
        <button
          onClick={handleDownload}
          data-testid="flow-download-png"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F0F0F]/95 hover:bg-[#1A1A1A] border border-white/10 rounded-lg text-xs font-semibold transition shadow-lg"
          style={{ color: accent || '#fff', borderColor: `${accent || '#2563EB'}44` }}
        >
          <Download size={11} weight="bold" /> PNG
        </button>
      </div>
      <div ref={flowRef} className="h-full w-full">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.3}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
          nodesDraggable
          nodesConnectable={false}
          elementsSelectable
        >
          <Background color="#222" gap={24} />
          <Controls
            showInteractive={false}
            position="bottom-left"
            style={{ background: '#0F0F0F', border: '1px solid #333', borderRadius: 8 }}
            className="funnel-flow-controls"
          />
        </ReactFlow>
      </div>
      {/* Override default dark-on-dark control icons */}
      <style>{`
        .funnel-flow-controls button { background: transparent !important; border-bottom: 1px solid #222 !important; }
        .funnel-flow-controls button:hover { background: #1A1A1A !important; }
        .funnel-flow-controls button svg, .funnel-flow-controls button path { fill: #fff !important; }
      `}</style>
    </>
  );
}

export default function FunnelFlowchart({ stages, title, testId = 'funnel-flowchart', height = '480px', accent = '#2563EB', downloadName }) {
  if (!stages || stages.length === 0) {
    return (
      <div
        data-testid={`${testId}-empty`}
        className="flex items-center justify-center bg-[#0A0A0A] border border-white/10 border-dashed rounded-xl text-white/40 text-sm"
        style={{ height }}
      >
        {i18n.t('flow.no_stages')}
      </div>
    );
  }
  return (
    <div data-testid={testId} className="relative bg-[#0A0A0A] border border-white/10 rounded-xl overflow-hidden" style={{ height }}>
      {title && (
        <div className="absolute top-3 left-3 z-10 px-3 py-1.5 bg-[#0F0F0F]/95 backdrop-blur-sm border border-white/10 rounded-lg text-xs font-semibold text-white/80">
          {title}
        </div>
      )}
      <ReactFlowProvider>
        <FlowchartInner stages={stages} accent={accent} downloadName={downloadName} />
      </ReactFlowProvider>
    </div>
  );
}

// ─── Stage builders (one per data source) ────────────────────────────


/** Convert an Auto-Build response into a 4-stage funnel: magnet → landing → email → pitch. */
export function autoBuildToStages(build) {
  if (!build) return [];
  const lm = build.lead_magnet || {};
  const lp = build.landing_copy || {};
  const emails = build.email_sequence || [];
  const stages = [];

  if (lm.hook_headline || lm.hook || lm.delivery_format) {
    stages.push({
      id: 'magnet',
      stage_type: 'magnet',
      label: lm.hook_headline || lm.hook || 'Lead Magnet',
      description: lm.delivery_format || lm.format,
      cta: lm.cta || i18n.t('flow.download_free'),
    });
  }
  if (lp.headline) {
    stages.push({
      id: 'landing',
      stage_type: 'landing',
      label: lp.headline,
      description: lp.subheadline,
      copy_excerpt: (lp.bullets || [])[0],
      cta: lp.cta || i18n.t('flow.optin'),
    });
  }
  if (emails && emails.length) {
    stages.push({
      id: 'emails',
      stage_type: 'email',
      label: emails[0]?.subject || i18n.t('flow.nurture_seq'),
      description: emails.length > 1 ? i18n.t('flow.seq_emails', { count: emails.length }) : i18n.t('flow.email_1'),
      copy_excerpt: (emails[0]?.body || '').slice(0, 120),
      meta: `${emails.length}x`,
    });
  }
  // Implicit pitch closing the funnel — picks the last email's CTA/body or the offer text.
  stages.push({
    id: 'pitch',
    stage_type: 'pitch',
    label: build.offer || i18n.t('flow.main_offer'),
    description: emails.length ? i18n.t('flow.pitch_in_email', { n: emails.length }) : i18n.t('flow.direct_pitch'),
    cta: i18n.t('flow.buy_now'),
  });
  return stages;
}


/** Convert a Competitor Cross-Analysis into a "attack plan" flow: common themes → whitespace → differentiation → quick wins. */
export function competitorAnalysisToStages(doc) {
  const a = doc?.analysis || {};
  const stages = [];
  if (a.executive_summary) {
    stages.push({
      id: 'summary',
      stage_type: 'landing',
      label: i18n.t('flow.exec_summary'),
      description: a.executive_summary,
    });
  }
  const themes = a.common_themes || [];
  if (themes.length) {
    stages.push({
      id: 'themes',
      stage_type: 'email',
      label: i18n.t('flow.saturated_themes'),
      description: themes.slice(0, 3).map((t) => t.theme).filter(Boolean).join(' · '),
      meta: `${themes.length}`,
    });
  }
  const gaps = a.white_space_gaps || [];
  if (gaps.length) {
    stages.push({
      id: 'gaps',
      stage_type: 'magnet',
      label: i18n.t('flow.whitespace'),
      description: gaps[0]?.gap,
      copy_excerpt: gaps[0]?.why_it_works,
      meta: `${gaps.length}`,
    });
  }
  const diff = a.differentiation_strategy || {};
  if (diff.positioning_statement || (diff.key_messages || []).length) {
    stages.push({
      id: 'positioning',
      stage_type: 'pitch',
      label: i18n.t('flow.positioning'),
      description: diff.positioning_statement,
      copy_excerpt: (diff.key_messages || [])[0],
    });
  }
  const wins = a.quick_wins || [];
  if (wins.length) {
    stages.push({
      id: 'wins',
      stage_type: 'thank_you',
      label: i18n.t('flow.quick_wins'),
      description: wins.slice(0, 3).join(' · '),
      meta: `${wins.length}`,
    });
  }
  return stages;
}
